import type { ConfirmParam } from '@src/types/Confirm';
import type { IAggFuncParams } from 'ag-grid-community';
import type Big from 'big.js';

export type PriceValue =
  | {
      /** The price of the asset in units of quoteCurrency */
      price: Big | undefined;
      /** The outer bound of a price band in units of quoteCurrency */
      endPrice: Big | undefined;
      /** The asset which is being priced */
      asset: string | undefined;
      /** Resolved quote currency symbol (e.g. USD) */
      quoteCurrency: string | undefined;
      /** Whether or not we can be aggregated */
      aggregate: boolean;
      isBestPrice: boolean;
      /** Whether the price is a rate, as in 0.01 = 1% */
      isRate: boolean | undefined;
      /** Treasury eligibility resolved independently of price identity. */
      shouldFormatAsUSTreasury: boolean | undefined;

      confirm: ConfirmParam | undefined;
    }
  | undefined;

// Just a helper type to grab the confirm without exposing weird ts necessities
export type PriceValueConfirm = NonNullable<PriceValue>['confirm'];

const EXIT_AGGREGATE: PriceValue = {
  price: undefined,
  endPrice: undefined,
  asset: undefined,
  quoteCurrency: undefined,
  aggregate: false,
  isBestPrice: false,
  isRate: false,
  shouldFormatAsUSTreasury: false,
  confirm: undefined,
};

export function priceAggFunc({ values: unsanitizedValues }: IAggFuncParams<unknown, PriceValue>): PriceValue {
  const values = unsanitizedValues.filter((value: PriceValue | null): value is PriceValue => value != null);
  return values.reduce((total, value) => {
    if (!value?.aggregate) {
      return EXIT_AGGREGATE;
    }

    // You cannot aggregate rate prices (yet)
    if (value.isRate) {
      return EXIT_AGGREGATE;
    }

    // Any price cells with a confirm related to it do not support aggregation. If we decide we need this then we need to do some big thinking
    if (value.confirm) {
      return EXIT_AGGREGATE;
    }

    // Check that value is sufficiently populated. If these properties are not defined, we cannot perform an aggregation.
    if (value.asset == null || value.price == null || value.quoteCurrency == null) {
      return EXIT_AGGREGATE;
    }

    // Instantiate total to value, would happen on the first go
    if (!total) {
      return value;
    }

    // If there is no value for whatever reason, continue
    if (!value) {
      return total;
    }

    // Reaching this point means that total is instantiated, and value is defined.

    // If the currencies dont match at this point, exit the aggregation
    if (total.quoteCurrency !== value.quoteCurrency || total.asset !== value.asset) {
      return EXIT_AGGREGATE;
    }

    // total is defined but it wasnt instantiated to have a value, just accept the value
    if (!total.price) {
      return value;
    }

    // total.price is defined, but value.price is not. This is fine since theyre the same currency (no exit) -> continue
    if (!value.price) {
      return total;
    }

    // both total price and value price are defined, lets ensure theyre the same in order to continue. if not -> exit
    if (!total.price.eq(value.price)) {
      return EXIT_AGGREGATE;
    }

    const shouldFormatAsUSTreasury = total.shouldFormatAsUSTreasury === true && value.shouldFormatAsUSTreasury === true;
    if ((value.shouldFormatAsUSTreasury === true) === shouldFormatAsUSTreasury) {
      return value;
    }

    return {
      ...value,
      shouldFormatAsUSTreasury,
    };
  }, undefined as PriceValue);
}
